import { Flex, Progress, Spacer, Text, useToast } from '@chakra-ui/react'
import {
  CheckBox,
  FileDrop,
  InputContainer,
  LoaderCover,
  ReadOnlyProperty,
  ReadOnlyPropertyProps,
  TextField,
} from 'components'
import Button from 'components/Button'
import DeleteConfirmButton from 'components/DeleteConfirmButton'
import IconButton from 'components/IconButton'
import Menu, { MenuOption } from 'components/Menu'
import Panel from 'components/Panel'
import {
  ArrowLongLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  EllipsisVerticalIcon,
} from 'components/icons'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import { where } from 'firebase/firestore'
import { lowerCase, startCase } from 'lodash'
import { useEffect, useState } from 'react'
import {
  ILeadLookupConfig,
  LookupOutputProp,
  LookupOutputProps,
  primaryLeadProperties,
} from 'types'
import { acceptedSpreadsheets, deleteDoc, getDocById, getDocs, useDoc } from 'utils'
import * as XLSX from 'xlsx'
import {
  cleanDuplicates,
  getSmartColumnsWithCharts,
  isSmartColumnInput,
  parseLookupValuesForUpdate,
  parseNewLookupValues,
  removeConfigFromWorkspace,
  updateLookUpConfig,
  updateLookupValues,
  useWorkspaceSmartColumns,
} from '../utils/lookupUtils'
import { SmartColumnAdditionalMapping } from './SmartColumnAdditionalMapping'

interface Props {
  lookUpId: string
}

export default function SmartColumnConfigurer({ lookUpId }: Props) {
  const [leadLookUpConfig] = useDoc('leadLookupConfig', lookUpId)
  const updated = leadLookUpConfig?.updated
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const [fileLoading, setFileLoading] = useState(false)
  const [buffer, setBuffer] = useState<ArrayBuffer>()
  const [dbTabs, setDbTabs] = useState<string[]>()
  const [selectedTab, setSelectedTab] = useState('')
  const [dbFields, setDbFields] = useState<string[]>()
  const [rawDbData, setRawDbData] = useState<Record<string, string>[]>()
  const [localConfig, setLocalConfig] = useState<Partial<ILeadLookupConfig>>()
  const [dbInputProp, setDbInputProp] = useState<string>()
  const [lookupOutputProps, setLookupOutputProps] = useState<LookupOutputProp[]>([
    { index: 0, dbField: '', dbFieldName: '', hasChart: false },
  ])
  const toast = useToast()
  const [nextId, setNextId] = useState(1)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string>('')
  const [saveEnabled, setSaveEnabled] = useState(false)
  const [editing, setEditing] = useState(false)
  const isUsedAsInput = isSmartColumnInput(
    leadLookUpConfig?.outputProperties?.map(prop => prop.name) || [],
    workspace?.id!
  )
  const { confirm } = useAlert()
  const rowLimit = 100000
  const momentifyFields = [...primaryLeadProperties, ...useWorkspaceSmartColumns(workspace?.id!)]
  const chartSmartColumns = getSmartColumnsWithCharts(lookUpId)

  useEffect(() => {
    if (!leadLookUpConfig) return
    setLocalConfig(leadLookUpConfig)
  }, [leadLookUpConfig])

  useEffect(() => {
    const outputProps = lookupOutputProps
      .map(row => ({
        name: row.dbFieldName !== '' ? row.dbFieldName : row.dbField,
        hasChart: row.hasChart,
      }))
      .filter(Boolean)
    setLocalConfig({
      ...localConfig,
      outputProperties: outputProps.map(prop => ({ name: prop.name, hasChart: prop.hasChart })),
    })
  }, [lookupOutputProps])

  useEffect(() => {
    if (!buffer) return
    const { SheetNames } = XLSX.read(buffer)
    setDbTabs(SheetNames)
    if (SheetNames.length == 1) setSelectedTab(SheetNames[0])
  }, [buffer])

  useEffect(() => {
    if (!selectedTab || !buffer) return
    const { Sheets } = XLSX.read(buffer)
    const sheet = Sheets[selectedTab]
    const data = XLSX.utils.sheet_to_json(sheet) as Record<string, string>[]
    if (data.length > rowLimit) {
      toast({ status: 'error', title: `File exceeds limit of ${rowLimit} rows`, isClosable: true })
      return
    }
    setRawDbData(data)
    setDbFields(Object.keys(data[0]))
  }, [selectedTab])

  useEffect(() => {
    if (error) {
      setSaveEnabled(false)
      updateLookUpConfig(user?.id!, lookUpId!, { status: 'invalid' })
      toast({ title: error, status: 'error', isClosable: true })
      setError('')
    } else setSaveEnabled(true)
  }, [error])

  useEffect(() => {
    if (progress === 100) {
      updateLookUpConfig(user!.id, lookUpId, { ...localConfig, status: 'success' })
      setEditing(false)
      resetData()
    } else if (progress > 0) setSaveEnabled(false)
  }, [progress])

  useEffect(() => {
    if (dbInputProp && rawDbData) {
      const { cleanedData, duplicatesNum } = cleanDuplicates(rawDbData, dbInputProp)
      if (duplicatesNum > 0) alertDuplicates(duplicatesNum, cleanedData)
    }
  }, [dbInputProp])

  function updateOutputProps({ index, updateOn, dbField, fieldName }: LookupOutputProps) {
    const updatedOutputProps = lookupOutputProps.map(row => {
      if (row.index !== index) return row

      switch (updateOn) {
        case 'field':
          return dbField ? { ...row, dbField } : row

        case 'fieldName':
          return fieldName ? { ...row, dbFieldName: fieldName } : row

        case 'hasChart':
          return { ...row, hasChart: !row.hasChart }

        default:
          return row
      }
    })

    setLookupOutputProps(updatedOutputProps)
  }

  function resetData() {
    setDbInputProp(undefined)
    setDbFields(undefined)
    setRawDbData(undefined)
    setLookupOutputProps([{ index: 0, dbField: '', dbFieldName: '', hasChart: false }])
    setLocalConfig({ ...localConfig, outputProperties: [] })
    setProgress(0)
  }

  async function resetConfig() {
    resetData()
    setEditing(false)
    const leadConfig = await getDocById('leadLookupConfig', lookUpId)
    if (leadConfig) setLocalConfig(leadConfig)
  }

  async function handleDataUpload(files: File[]) {
    try {
      if (!files?.length) throw 'File type not accepted'
      setFileLoading(true)
      setBuffer(await files[0].arrayBuffer())
      setFileLoading(false)
    } catch (error: any) {
      console.error(error)
      toast({ title: error.message, status: 'error', isClosable: true })
    }
  }

  function handleAddField() {
    setLookupOutputProps(prev => [
      ...(prev || []),
      { index: nextId, dbField: '', dbFieldName: '', hasChart: false },
    ])
    setNextId(prev => prev + 1)
  }

  function handleRemoveRow(rowId: number) {
    setLookupOutputProps(prev => prev.filter(row => row.index !== rowId))
  }

  async function handleSave() {
    if (!user || !localConfig || !rawDbData) return
    let lookupValueDocs
    if (editing) {
      const leadLookupValues = await getDocs(
        'leadLookupValues',
        0,
        where('configId', '==', lookUpId)
      )
      const { batchRows, newRows, updatedRows, deletedRows } = parseLookupValuesForUpdate(
        rawDbData,
        localConfig.inputProperty!,
        dbInputProp!,
        localConfig.outputProperties!.map(prop => prop.name),
        lookupOutputProps.map(row => row.dbField),
        lookUpId,
        workspace?.id!,
        leadLookupValues
      )
      lookupValueDocs = batchRows
      toast({
        title: `${newRows} new rows, ${updatedRows} updated rows, ${deletedRows} deleted rows`,
        status: 'success',
        isClosable: true,
      })
    } else
      lookupValueDocs = parseNewLookupValues(
        rawDbData,
        localConfig.inputProperty!,
        dbInputProp!,
        localConfig.outputProperties!.map(prop => prop.name),
        lookupOutputProps.map(row => row.dbField),
        lookUpId,
        workspace?.id!
      )
    updateLookupValues(user.id, lookupValueDocs, setProgress, setError)
  }

  function handleDelete() {
    removeConfigFromWorkspace(user?.id!, workspace!, lookUpId!)
    deleteDoc('leadLookupConfig', lookUpId!)
  }

  async function alertDuplicates(duplicatesNum: number, cleanedData?: Record<string, string>[]) {
    const confirmed = await confirm(
      `This input field contains ${duplicatesNum} duplicate values. If you wish to continue with using the first value found for each duplicate, click "Confirm", or click "Cancel" and reupload a cleaned data file.`
    )
    if (confirmed) setRawDbData(cleanedData)
    else resetData()
  }

  function inputFieldCheck(value: string) {
    if (momentifyFields.map(field => lowerCase(String(field))).includes(lowerCase(value))) {
      toast({
        title: 'This field is already being used as a Momentify field. Enter a unique field name.',
        status: 'error',
        isClosable: true,
      })
      setSaveEnabled(false)
    } else setSaveEnabled(true)
  }

  const menuOptions: MenuOption[] = []
  if (leadLookUpConfig?.outputProperties) {
    menuOptions.push(
      {
        type: 'item',
        value: 'replaceData',
        isDisabled: isUsedAsInput,
        label: (
          <Flex
            gap={3}
            align="center"
            px={2}
            onClick={() => {
              setDbInputProp('')
              setLookupOutputProps([{ index: 0, dbField: '', dbFieldName: '', hasChart: false }])
              setEditing(true)
            }}
          >
            <ArrowUpTrayIcon fontSize="lg" />
            <Flex direction="column" gap={1}>
              <Text textStyle="bodySmall">Update Data</Text>
              {leadLookUpConfig.status === 'success' && updated && (
                <Text textStyle="bodyXSmall" opacity={0.6}>
                  Last updated: {new Date(updated).toLocaleString()}
                </Text>
              )}
            </Flex>
          </Flex>
        ),
      },
      { type: 'divider', m: 3 }
    )
  }
  menuOptions.push({
    type: 'custom',
    label: (
      <Flex direction="column" gap={3} align="flex-start">
        <DeleteConfirmButton
          isDisabled={isUsedAsInput}
          onDelete={handleDelete}
          description={
            localConfig?.status !== 'intialized'
              ? `This will delete the configuration for the '${startCase(
                  localConfig?.inputProperty || 'smart field'
                )}' as well as update all leads in live events with this change. Type "Delete" to confirm you wish to continue`
              : 'Type "Delete" to confirm'
          }
        />
        {isUsedAsInput && (
          <Text textStyle="bodyXSmall" opacity={0.6} w="280px" px={4}>
            This field is used as an input field for another smart column and cannot be modified
            until that depencency is removed
          </Text>
        )}
      </Flex>
    ),
  })

  if (!user || !workspace || !lookUpId) return null

  return (
    <LoaderCover show={fileLoading}>
      <Panel p={4} pt={6} gap={6}>
        {lookUpId && (
          <Flex direction="column" overflow="hidden" gap={6}>
            <Flex align="flex-end" gap={{ base: 1, lg: 6 }}>
              {!leadLookUpConfig?.outputProperties || editing ? (
                <>
                  <Menu
                    variant="outline"
                    menuProps={{
                      placement: 'auto',
                      matchWidth: true,
                    }}
                    w={optionWidths}
                    borderRadius="10px"
                    label="Momentify Field"
                    isDisabled={Boolean(progress)}
                    value={localConfig?.inputProperty}
                    options={momentifyFields.map(property => ({
                      type: 'item',
                      label: startCase(property),
                      value: property,
                    }))}
                    onChange={value => {
                      setLocalConfig({ ...localConfig, inputProperty: value })
                    }}
                  />
                  <FileDrop onDrop={handleDataUpload} options={{ accept: acceptedSpreadsheets }}>
                    {({ open }) => (
                      <Button
                        variant="outline"
                        isDisabled={!localConfig?.inputProperty || Boolean(progress)}
                        leftIcon={
                          dbFields ? (
                            <ArrowPathIcon fontSize="lg" />
                          ) : (
                            <ArrowUpTrayIcon fontSize="lg" />
                          )
                        }
                        onClick={() => {
                          open()
                          resetData()
                        }}
                      >
                        {`${dbFields || leadLookUpConfig?.inputProperty ? 'Replace' : 'Add'} Data`}
                      </Button>
                    )}
                  </FileDrop>
                  {!dbFields && leadLookUpConfig?.outputProperties && (
                    <Button w="80px" variant="outline" onClick={resetConfig}>
                      Cancel
                    </Button>
                  )}
                </>
              ) : (
                <Flex align="flex-end" gap={6} overflowX="auto">
                  <ReadOnlyProperty
                    name="Momentify Field"
                    value={leadLookUpConfig?.inputProperty!}
                    textProps={readOnlyTextProps}
                    formatReadOnlyValue={formatReadOnlyValue}
                  />
                  <ArrowLongLeftIcon transform="rotateY(180deg)" mb={2} />
                  <ReadOnlyProperty
                    name={
                      leadLookUpConfig?.outputProperties.length > 1
                        ? 'Mapped Fields'
                        : 'Mapped Field'
                    }
                    value={
                      leadLookUpConfig?.outputProperties?.map(prop => prop.name).join(', ') ?? ''
                    }
                    textProps={readOnlyTextProps}
                    formatReadOnlyValue={formatReadOnlyValue}
                  />
                  <ReadOnlyProperty
                    name={`Field${chartSmartColumns?.length! > 1 ? 's' : ''} with charts`}
                    value={chartSmartColumns!.join(', ') ?? ''}
                    textProps={readOnlyTextProps}
                    formatReadOnlyValue={formatReadOnlyValue}
                  />
                </Flex>
              )}
              <Spacer />
              <Menu
                variant="ghost"
                buttonLabel={<EllipsisVerticalIcon />}
                menuProps={{
                  placement: 'bottom-start',
                  autoSelect: false,
                }}
                as={IconButton}
                options={menuOptions}
              />
            </Flex>
            {dbTabs && (
              <Flex gap={5} align="center">
                <Text textStyle="bodySmall" opacity={0.7}>
                  Database Tab:
                </Text>
                <Menu
                  variant="outline"
                  borderRadius="10px"
                  menuProps={{ matchWidth: true }}
                  value={selectedTab}
                  options={dbTabs.map(tab => ({ label: tab, value: tab }))}
                  onChange={value => setSelectedTab(value)}
                />
              </Flex>
            )}

            {dbFields && (
              <Flex direction="column" gap={3}>
                <Flex align="flex-end" gap={5} pb={1}>
                  <Menu
                    variant="outline"
                    menuProps={{
                      placement: 'auto',
                      matchWidth: true,
                    }}
                    w={optionWidths}
                    borderRadius="10px"
                    label="Database Field"
                    isDisabled={Boolean(progress)}
                    value={dbInputProp}
                    options={dbFields.map(property => ({
                      type: 'item',
                      label: startCase(property),
                      value: property,
                    }))}
                    onChange={value => {
                      setDbInputProp(value)
                    }}
                  />
                  <ArrowLongLeftIcon transform="rotateY(180deg)" mb={2} />
                  <Menu
                    variant="outline"
                    menuProps={{
                      matchWidth: true,
                    }}
                    isDisabled={!dbInputProp || Boolean(progress)}
                    w={optionWidths}
                    borderRadius="10px"
                    label="Mapped Field"
                    value={lookupOutputProps[0] ? lookupOutputProps[0].dbField : ''}
                    options={dbFields
                      .filter(key => key !== localConfig?.inputProperty && key !== dbInputProp)
                      .map(property => ({
                        type: 'item',
                        label: startCase(property),
                        value: property,
                      }))}
                    onChange={value => {
                      updateOutputProps({ index: 0, updateOn: 'field', dbField: value })
                    }}
                  />
                  <InputContainer label="New Mapped Field Name" px={1}>
                    <TextField
                      size="sm"
                      h="40px"
                      isDisabled={!lookupOutputProps[0] || Boolean(progress)}
                      w={optionWidths}
                      borderRadius="10px"
                      placeholder="Enter field name..."
                      defaultValue=""
                      onChange={e => {
                        inputFieldCheck(e.target.value)
                        updateOutputProps({
                          index: 0,
                          updateOn: 'fieldName',
                          fieldName: e.target.value,
                        })
                      }}
                    />
                  </InputContainer>
                  <InputContainer label="Use for Analytics Charts" px={1} align="center" h="65px">
                    <Flex h="40px" align="center" justify="center">
                      <CheckBox
                        w="25px"
                        h="25px"
                        isChecked={!!lookupOutputProps[0].hasChart}
                        onChange={() =>
                          updateOutputProps({
                            index: 0,
                            updateOn: 'hasChart',
                          })
                        }
                      />
                    </Flex>
                  </InputContainer>
                </Flex>
                {lookupOutputProps.slice(1).map(row => (
                  <SmartColumnAdditionalMapping
                    key={row.index}
                    rowKey={row.index}
                    dbInputProp={dbInputProp!}
                    lookupOutputProps={lookupOutputProps}
                    dbFields={dbFields}
                    handleRemoveRow={handleRemoveRow}
                    updateOutputProps={updateOutputProps}
                  />
                ))}
                {lookupOutputProps[lookupOutputProps.length - 1].dbField &&
                  lookupOutputProps.length < dbFields.length - 1 && (
                    <Button
                      variant="outline"
                      w={optionWidths}
                      ml={{ base: 'auto', lg: '322px' }}
                      onClick={handleAddField}
                      isDisabled={Boolean(progress)}
                    >
                      Add Field
                    </Button>
                  )}
                <Flex gap={3}>
                  <Spacer />
                  <Button w="80px" variant="outline" onClick={resetConfig}>
                    Cancel
                  </Button>
                  <Button
                    w="80px"
                    variant="solid"
                    palette="primary"
                    onClick={handleSave}
                    isDisabled={
                      !dbInputProp ||
                      !localConfig?.inputProperty ||
                      localConfig.outputProperties?.length == 0 ||
                      !saveEnabled
                    }
                  >
                    Save
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>
        )}
        {progress > 0 && (
          <Flex gap={2} align="center">
            <Progress
              h="5px"
              w="100%"
              background="gray.500"
              borderRadius="999px"
              colorScheme="secondary"
              sx={{ '& > div': { transition: 'width 100ms ease' } }}
              value={progress}
            />
            <Text textStyle="bodyXSmall">{Math.floor(progress)}%</Text>
          </Flex>
        )}
      </Panel>
    </LoaderCover>
  )
}

function formatReadOnlyValue({ name, value }: ReadOnlyPropertyProps) {
  return name !== 'Momentify Field' ? value : startCase(value)
}

const optionWidths = { base: '100%', lg: '250px' }

const readOnlyTextProps = {
  w: optionWidths,
  fontWeight: 'semibold',
  isTruncated: true,
  p: 3,
}
